import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import styled from "styled-components";
import { baseUrl } from "../constants/url";

const RECEIPT_DISPLAY_MS = 12000;

const Container = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  flex-direction: column;
  gap: 1rem;
`;

const Loader = styled.div`
  width: 50px;
  height: 50px;
  border: 5px solid #f3f3f3;
  border-top: 5px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const Message = styled.div`
  font-size: 1rem;
  color: #333;
`;

const ReceiptWrapper = styled.div`
  width: 100%;
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const SuccessBanner = styled.div`
  background: #e6f7ec;
  color: #1f7a3a;
  border-bottom: 1px solid #c6e9d2;
  padding: 12px 16px;
  font-size: 1rem;
  font-weight: 600;
  text-align: center;
`;

const ReceiptFrame = styled.iframe`
  flex: 1;
  width: 100%;
  border: 0;
`;

const PaymentSuccess = () => {
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [receiptHtml, setReceiptHtml] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  const sendMessageToReactNative = (status, data) => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({
        type: 'PAYMENT_RESULT',
        status,
        data
      }));
    }
  };

  const closeWebView = () => {
    if (window.ReactNativeWebView) {
      window.ReactNativeWebView.postMessage(JSON.stringify({ type: 'CLOSE_WEBVIEW' }));
    } else {
      window.location.assign("https://app.summairsportsclub.org");
    }
  };

  const getPaymentStatusMessage = (status) => {
    if (status === "Success") {
      return "Payment completed successfully";
    } else if (status === "Aborted") {
      return "Payment cancel";
    } else {
      return "Payment failed";
    }
  }

  useEffect(() => {
    const updatePaymentStatus = async () => {
      try {
        const params = new URLSearchParams(location.search);
        const userToken = params.get("userToken");
        const order_id = params.get("order_id");
        const memberCode = params.get("memberCode");
        const memberId = params.get("memberId");
        const status = params.get("status");
        const payment_receipt_id = params.get("payment_receipt_id");
        setStatus(status)

        const getPaymentStatus = (status) => {
          if (status === "Success") {
            return "success";
          } else if (status === "Aborted") {
            return "cancel";
          } else {
            return "failed";
          }
        }

        // Send message to React Native
        sendMessageToReactNative(getPaymentStatus(status), {
          order_id,
          message: getPaymentStatusMessage(status)
        });

        let receiptShown = false;
        if (status === "Success" && payment_receipt_id && userToken && memberId && memberCode) {
          try {
            // Local date in YYYY-MM-DD (toISOString would shift to UTC)
            const now = new Date();
            const receiptDate = [
              now.getFullYear(),
              String(now.getMonth() + 1).padStart(2, "0"),
              String(now.getDate()).padStart(2, "0"),
            ].join("-");
            const resp = await axios.get(
              `${baseUrl}/GenerateReceipt/${payment_receipt_id}/${receiptDate}`,
              {
                headers: {
                  Authorization: `Bearer ${userToken}`,
                  MemberId: memberId,
                  MemberCode: memberCode,
                },
                transformResponse: [(d) => d],
              }
            );
            const responseInJson = JSON.parse(resp.data);
            if (responseInJson.isSuccess  && responseInJson.result) {
              setReceiptHtml(responseInJson.result);
              receiptShown = true;
            }
          } catch (e) {
            console.log("GenerateReceipt fetch failed:", e?.response?.status, e?.message);
          }
        }

        const closeDelay = receiptShown ? RECEIPT_DISPLAY_MS : 5000;
        setTimeout(closeWebView, closeDelay);

        setLoading(false);
      } catch (err) {
        console.error("Payment status update error:", err);
        setError(err.message);
        sendMessageToReactNative('error', { message: err.message });
        setLoading(false);
      }
    };

    updatePaymentStatus();
  }, [location]);

  if (loading) {
    return (
      <Container>
        <Loader />
        <Message>Updating payment status...</Message>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Message style={{ color: "red" }}>Error: {error}</Message>
      </Container>
    );
  }

  if (receiptHtml) {
    return (
      <ReceiptWrapper>
        <SuccessBanner>{getPaymentStatusMessage(status)}</SuccessBanner>
        <ReceiptFrame title="receipt" srcDoc={receiptHtml} sandbox="allow-same-origin" />
      </ReceiptWrapper>
    );
  }

  return (
    <Container>
      <Loader />
      <Message>{getPaymentStatusMessage(status)}! Redirecting...</Message>
    </Container>
  );
};

export default PaymentSuccess;